// src/pages/work-order-management-system/components/BudgetModal.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/appIcon';
import Input from '../../../components/ui/Input';

import { useResponsiveModalLayout } from './hooks/useResponsiveModalLayout';
import { useBudgetForm } from './hooks/useBudgetForm';
import { useBudgetItems } from './hooks/useBudgetItems';
import { useBudgetTotals } from './hooks/useBudgetTotals';
import { useBudgetPdf } from './hooks/useBudgetPdf';

const BUDGET_SECTIONS = [
    { id: 'orden', label: 'Datos del servicio', description: 'Cliente, unidad, folio y observaciones', icon: 'User' },
    { id: 'servicios', label: 'Servicios', description: 'Mano de obra y diagnósticos', icon: 'Wrench' },
    { id: 'refacciones', label: 'Refacciones', description: 'Piezas, componentes y materiales', icon: 'Package' },
    { id: 'resumen', label: 'Resumen y totales', description: 'Impuestos y total a cobrar', icon: 'Calculator' }
];

const BudgetModal = ({
    isOpen,
    onClose,
    selectedWorkOrder = null,
    initialData = null, // ✅ editar desde historial
    inventory = [],
    workOrders = []
}) => {
    // Layout
    const { isMobile, windowHeight } = useResponsiveModalLayout();

    // ✅ prioridad: initialData (historial) > selectedWorkOrder (backend)
    const prefill = initialData || selectedWorkOrder || null;
    const isEditing = Boolean(initialData || selectedWorkOrder);

    // Form
    const { datosGenerales, handleChangeDatosGenerales, folio } = useBudgetForm(selectedWorkOrder, prefill);

    // Items
    const {
        refacciones,
        servicios,
        nuevaRefaccion,
        setNuevaRefaccion,
        nuevoServicio,
        setNuevoServicio,
        handleAddRefaccion,
        handleAddServicio,
        handleRemoveRefaccion,
        handleRemoveServicio,
        handleUpdateRefaccion,
        handleUpdateServicio,
        displayNumber,
        tieneItems
    } = useBudgetItems(prefill);

    // Totales / impuestos
    const { incluirIVA, setIncluirIVA, incluirISR, setIncluirISR, calculosOrden } =
        useBudgetTotals(refacciones, servicios);

    // PDF
    const { generarPDF } = useBudgetPdf({
        datosGenerales,
        refacciones,
        servicios,
        calculosOrden,
        incluirIVA,
        incluirISR
    });

    /* =====================================================
     * helpers anti-zoom / keyboard dismiss
     * ===================================================== */
    const dismissKeyboard = useCallback(() => {
        if (typeof document === 'undefined') return;
        const el = document.activeElement;
        if (el && typeof el.blur === 'function') el.blur();
    }, []);

    const handleSafeClose = useCallback(() => {
        dismissKeyboard();
        onClose?.();
    }, [dismissKeyboard, onClose]);

    /* =====================================================
     * SCROLL SYSTEM (una sola pantalla)
     * ===================================================== */
    const scrollRef = useRef(null);

    const sectionRefs = useMemo(() => {
        const refs = {};
        BUDGET_SECTIONS.forEach((s) => {
            refs[s.id] = React.createRef();
        });
        return refs;
    }, []);

    const [activeSection, setActiveSection] = useState(BUDGET_SECTIONS[0].id);
    const [progress, setProgress] = useState(0);

    const scrollToSection = useCallback(
        (id) => {
            // ✅ al cambiar de sección, quita teclado para que NO haga zoom/brinco
            dismissKeyboard();

            const el = sectionRefs[id]?.current;
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        [dismissKeyboard, sectionRefs]
    );

    // progress por scroll
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const onScroll = () => {
            const max = el.scrollHeight - el.clientHeight;
            const pct = max > 0 ? Math.round((el.scrollTop / max) * 100) : 0;
            setProgress(Math.max(0, Math.min(100, pct)));
        };

        onScroll();
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    // scrollspy (sección visible)
    useEffect(() => {
        const root = scrollRef.current;
        if (!root) return;

        const obs = new IntersectionObserver(
            (entries) => {
                const best = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];

                if (best?.target?.dataset?.sectionId) {
                    setActiveSection(best.target.dataset.sectionId);
                }
            },
            {
                root,
                threshold: [0.15, 0.3, 0.6],
                rootMargin: '-10% 0px -70% 0px'
            }
        );

        BUDGET_SECTIONS.forEach((s) => {
            const el = sectionRefs[s.id]?.current;
            if (el) obs.observe(el);
        });

        return () => obs.disconnect();
    }, [sectionRefs]);

    if (!isOpen) return null;

    const totalActual = Number(calculosOrden?.total || 0);
    const subtotal = Number(calculosOrden?.subtotal || 0);
    const iva = Number(calculosOrden?.iva || 0);
    const isr = Number(calculosOrden?.isr || 0);

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 p-0 safe-area-inset overflow-hidden">
                {/* ✅ Estilos SOLO dentro del modal (sin jsx global / sin warning) */}
                <style>{`
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

          /* ✅ Anti-zoom iOS: cualquier input/textarea dentro del modal en mobile => >= 16px */
          @media (max-width: 640px) {
            .modal-no-zoom input,
            .modal-no-zoom textarea,
            .modal-no-zoom select,
            .modal-no-zoom button {
              font-size: 16px !important;
            }
          }
        `}</style>

                {/* ✅ Overlay (cierra teclado + cierra modal) */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-gradient-to-b from-gray-700/60 via-gray-800/55 to-gray-900/60 backdrop-blur-lg"
                    onClick={handleSafeClose}
                />

                {/* Contenedor principal */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    transition={{ type: 'spring', damping: 24, stiffness: 260 }}
                    className="modal-no-zoom relative z-10 w-full h-full bg-gray-950/98 border border-gray-900/80 shadow-[0_0_0_1px_rgba(15,23,42,0.9),0_40px_120px_rgba(0,0,0,0.95)] flex flex-col overflow-hidden"
                    style={isMobile ? { height: `${windowHeight}px` } : { height: '100vh' }}
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Presupuesto / Orden de trabajo"
                >
                    {/* Header */}
                    <div className="flex items-start lg:items-center justify-between gap-3 px-4 lg:px-6 py-4 border-b border-gray-800 bg-gradient-to-r from-gray-950 via-gray-900 to-gray-900 pt-12 lg:pt-4">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">
                                    {isEditing ? 'Orden de trabajo' : 'Nueva orden de trabajo'}
                                </h1>
                                <span className="inline-flex text-[11px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/40">
                                    FOLIO: {folio}
                                </span>
                            </div>
                            <p className="text-cyan-300 flex items-center gap-2 text-xs sm:text-sm lg:text-base truncate">
                                <Icon name="Navigation" size={14} className="lg:w-4 lg:h-4 flex-shrink-0" />
                                <span className="truncate">
                                    {BUDGET_SECTIONS.find((s) => s.id === activeSection)?.label} • Todo en una sola pantalla
                                </span>
                            </p>
                        </div>

                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <div className="hidden sm:flex flex-col items-end text-[11px] text-gray-400">
                                <span>
                                    Cliente:{' '}
                                    <span className="text-gray-200 font-medium">{datosGenerales.cliente || 'Sin definir'}</span>
                                </span>
                                <span>
                                    Total actual:{' '}
                                    <span className="text-cyan-300 font-semibold">${totalActual.toFixed(2)}</span>
                                </span>
                            </div>

                            <button
                                onClick={handleSafeClose}
                                className="p-2 lg:p-3 hover:bg-gray-900 rounded-xl transition-colors group active:scale-95"
                                type="button"
                            >
                                <Icon name="X" size={20} className="lg:w-6 lg:h-6 text-gray-400 group-hover:text-white" />
                            </button>
                        </div>
                    </div>

                    {/* ✅ Navegación rápida (chips) */}
                    <div className="px-3 sm:px-4 lg:px-6 py-3 border-b border-gray-900/80 bg-gray-950/90">
                        <div className="flex gap-2 overflow-x-auto no-scrollbar">
                            {BUDGET_SECTIONS.map((section) => (
                                <button
                                    key={section.id}
                                    onClick={() => scrollToSection(section.id)}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-full border text-xs sm:text-sm whitespace-nowrap transition active:scale-95 ${activeSection === section.id
                                            ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-200'
                                            : 'bg-gray-900/40 border-gray-800 text-gray-300 hover:bg-gray-900'
                                        }`}
                                    type="button"
                                >
                                    <Icon
                                        name={section.icon}
                                        size={14}
                                        className={activeSection === section.id ? 'text-cyan-300' : 'text-gray-400'}
                                    />
                                    {section.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Progreso por scroll */}
                    <div className="px-4 lg:px-6 pt-3 lg:pt-4 bg-gray-950/95 border-b border-gray-900/80">
                        <div className="flex items-center justify-between mb-1 lg:mb-2">
                            <span className="text-xs lg:text-sm text-cyan-300 font-medium">Progreso</span>
                            <span className="text-xs lg:text-sm font-medium text-cyan-300">{progress}%</span>
                        </div>
                        <div className="w-full bg-gray-900 rounded-full h-1.5 lg:h-2 mb-3 border border-gray-800/80 overflow-hidden">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${progress}%` }}
                                transition={{ duration: 0.3, ease: 'easeOut' }}
                                className="bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 h-1.5 lg:h-2 rounded-full shadow-[0_0_18px_rgba(56,189,248,0.4)]"
                            />
                        </div>
                    </div>

                    {/* BODY SCROLL */}
                    <div
                        ref={scrollRef}
                        className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4 lg:p-8 safe-area-inset-bottom space-y-8 lg:space-y-10"
                        // ✅ si empiezas a scrollear, adiós teclado
                        onScrollCapture={() => {
                            if (isMobile) dismissKeyboard();
                        }}
                    >
                        {/* ========= ORDEN ========= */}
                        <section ref={sectionRefs.orden} data-section-id="orden" className="scroll-mt-28">
                            <div className="bg-gray-900/80 border border-gray-800/90 rounded-2xl p-4 sm:p-5 shadow-inner shadow-black/40 space-y-3">
                                <div className="flex items-center justify-between gap-3 mb-1">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-xl bg-cyan-600/20 flex items-center justify-center text-cyan-300 border border-cyan-500/40">
                                            <Icon name="User" className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm sm:text-base font-semibold text-white">Datos del servicio</h3>
                                            <p className="text-[11px] text-gray-400">Información que aparecerá en la cabecera del PDF.</p>
                                        </div>
                                    </div>
                                    <div className="hidden sm:flex flex-col items-end text-[11px] text-gray-400">
                                        <span>Fecha: {datosGenerales.fecha || '—'}</span>
                                        <span>Unidad: {datosGenerales.unidad || 'Sin definir'}</span>
                                    </div>
                                </div>

                                <div className="grid gap-3 md:grid-cols-3">
                                    <Input
                                        label="Cliente"
                                        value={datosGenerales.cliente}
                                        onChange={(e) => handleChangeDatosGenerales('cliente', e.target.value)}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="Unidad"
                                        value={datosGenerales.unidad}
                                        onChange={(e) => handleChangeDatosGenerales('unidad', e.target.value)}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="Motor"
                                        value={datosGenerales.motor}
                                        onChange={(e) => handleChangeDatosGenerales('motor', e.target.value)}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="Modelo"
                                        value={datosGenerales.modelo}
                                        onChange={(e) => handleChangeDatosGenerales('modelo', e.target.value)}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="VIN"
                                        value={datosGenerales.vin}
                                        onChange={(e) => handleChangeDatosGenerales('vin', e.target.value.toUpperCase())}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="Placas"
                                        value={datosGenerales.placas}
                                        onChange={(e) => handleChangeDatosGenerales('placas', e.target.value.toUpperCase())}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="Kilometraje"
                                        type="number"
                                        value={datosGenerales.kilometraje}
                                        onChange={(e) => handleChangeDatosGenerales('kilometraje', e.target.value)}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="Tipo de servicio"
                                        value={datosGenerales.tipoServicio}
                                        onChange={(e) => handleChangeDatosGenerales('tipoServicio', e.target.value)}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="Fecha"
                                        type="date"
                                        value={datosGenerales.fecha}
                                        onChange={(e) => handleChangeDatosGenerales('fecha', e.target.value)}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="Folio"
                                        value={datosGenerales.factura}
                                        onChange={(e) => handleChangeDatosGenerales('factura', e.target.value)}
                                        placeholder="Folio interno / externo"
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                </div>
                            </div>

                            <div className="bg-gray-900/80 border border-gray-800/90 rounded-2xl p-4 sm:p-5 shadow-inner shadow-black/40 mt-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-8 h-8 rounded-xl bg-emerald-600/20 flex items-center justify-center text-emerald-300 border border-emerald-500/40">
                                        <Icon name="FileText" className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm sm:text-base font-semibold text-white">Observaciones</h3>
                                        <p className="text-[11px] text-gray-400">Notas internas o comentarios para el cliente.</p>
                                    </div>
                                </div>

                                <textarea
                                    value={datosGenerales.observaciones}
                                    onChange={(e) => handleChangeDatosGenerales('observaciones', e.target.value)}
                                    className="w-full h-28 sm:h-32 bg-gray-950/50 border border-gray-800 rounded-xl text-base sm:text-sm text-white p-3 focus:outline-none focus:ring-2 focus:ring-cyan-500/60 resize-none"
                                    placeholder="Observaciones adicionales, condiciones, aclaraciones, etc."
                                />
                            </div>
                        </section>

                        {/* ========= SERVICIOS ========= */}
                        <section ref={sectionRefs.servicios} data-section-id="servicios" className="scroll-mt-28">
                            <div className="bg-gray-900/85 border border-gray-800 rounded-2xl p-4 sm:p-5 shadow-inner shadow-black/40">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-8 h-8 rounded-xl bg-emerald-600/20 flex items-center justify-center text-emerald-300 border border-emerald-500/40">
                                        <Icon name="Wrench" className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm sm:text-base font-semibold text-white">Agregar servicio</h3>
                                        <p className="text-[11px] text-gray-400">Mano de obra, diagnósticos, paquetes, etc.</p>
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-4 gap-3">
                                    <Input
                                        label="Descripción"
                                        value={nuevoServicio.descripcion}
                                        onChange={(e) => setNuevoServicio((prev) => ({ ...prev, descripcion: e.target.value }))}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="Cantidad"
                                        type="number"
                                        value={displayNumber(nuevoServicio.cantidad)}
                                        onChange={(e) => setNuevoServicio((prev) => ({ ...prev, cantidad: Number(e.target.value) || 0 }))}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="Precio unitario"
                                        type="number"
                                        value={displayNumber(nuevoServicio.precioUnitario)}
                                        onChange={(e) =>
                                            setNuevoServicio((prev) => ({ ...prev, precioUnitario: Number(e.target.value) || 0 }))
                                        }
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <div className="flex items-end">
                                        <Button
                                            type="button"
                                            onClick={() => {
                                                dismissKeyboard();
                                                handleAddServicio();
                                            }}
                                            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-xs sm:text-sm shadow-lg shadow-cyan-500/30"
                                        >
                                            <Icon name="Plus" className="w-4 h-4 mr-1" />
                                            Agregar servicio
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-900/85 border border-gray-800 rounded-2xl p-4 sm:p-5 shadow-inner shadow-black/40 mt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm sm:text-base font-semibold text-white">Servicios agregados</h3>
                                    <span className="text-[11px] text-gray-400">{servicios.length} servicio(s)</span>
                                </div>

                                {servicios.length === 0 ? (
                                    <p className="text-xs text-gray-500">No hay servicios agregados.</p>
                                ) : (
                                    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                                        {servicios.map((serv) => (
                                            <div
                                                key={serv.id}
                                                className="flex items-start gap-2 bg-gray-950/60 rounded-xl p-2.5 text-xs border border-gray-800"
                                            >
                                                <div className="flex-1 space-y-1.5">
                                                    <Input
                                                        label="Descripción"
                                                        value={serv.descripcion}
                                                        onChange={(e) => handleUpdateServicio(serv.id, 'descripcion', e.target.value)}
                                                        className="bg-gray-950 border-gray-800 text-base sm:text-[11px]"
                                                    />
                                                    <div className="grid grid-cols-3 gap-2">
                                                        <Input
                                                            label="Cant."
                                                            type="number"
                                                            value={displayNumber(serv.cantidad)}
                                                            onChange={(e) => handleUpdateServicio(serv.id, 'cantidad', Number(e.target.value) || 0)}
                                                            className="bg-gray-950 border-gray-800 text-base sm:text-[11px]"
                                                        />
                                                        <Input
                                                            label="Precio"
                                                            type="number"
                                                            value={displayNumber(serv.precioUnitario)}
                                                            onChange={(e) =>
                                                                handleUpdateServicio(serv.id, 'precioUnitario', Number(e.target.value) || 0)
                                                            }
                                                            className="bg-gray-950 border-gray-800 text-base sm:text-[11px]"
                                                        />
                                                        <div className="flex flex-col justify-end items-end text-[11px]">
                                                            <span className="text-gray-400">Total</span>
                                                            <span className="font-semibold text-white">${Number(serv.total || 0).toFixed(2)}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        dismissKeyboard();
                                                        handleRemoveServicio(serv.id);
                                                    }}
                                                    className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg flex-shrink-0 mt-5"
                                                >
                                                    <Icon name="Trash2" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* ========= REFACCIONES ========= */}
                        <section ref={sectionRefs.refacciones} data-section-id="refacciones" className="scroll-mt-28">
                            <div className="bg-gray-900/85 border border-gray-800 rounded-2xl p-4 sm:p-5 shadow-inner shadow-black/40">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-8 h-8 rounded-xl bg-blue-600/20 flex items-center justify-center text-blue-300 border border-blue-500/40">
                                        <Icon name="Package" className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm sm:text-base font-semibold text-white">Agregar refacción</h3>
                                        <p className="text-[11px] text-gray-400">Ítems rápidos para el presupuesto.</p>
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-4 gap-3">
                                    <Input
                                        label="Nombre"
                                        value={nuevaRefaccion.nombre}
                                        onChange={(e) => setNuevaRefaccion((prev) => ({ ...prev, nombre: e.target.value }))}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="Cantidad"
                                        type="number"
                                        value={displayNumber(nuevaRefaccion.cantidad)}
                                        onChange={(e) => setNuevaRefaccion((prev) => ({ ...prev, cantidad: Number(e.target.value) || 0 }))}
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <Input
                                        label="Precio unitario"
                                        type="number"
                                        value={displayNumber(nuevaRefaccion.precioUnitario)}
                                        onChange={(e) =>
                                            setNuevaRefaccion((prev) => ({ ...prev, precioUnitario: Number(e.target.value) || 0 }))
                                        }
                                        className="bg-gray-950/40 border-gray-800 text-white font-semibold text-base sm:text-sm"
                                    />
                                    <div className="flex items-end">
                                        <Button
                                            type="button"
                                            onClick={() => {
                                                dismissKeyboard();
                                                handleAddRefaccion();
                                            }}
                                            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-xs sm:text-sm shadow-lg shadow-cyan-500/30"
                                        >
                                            <Icon name="Plus" className="w-4 h-4 mr-1" />
                                            Agregar refacción
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-900/85 border border-gray-800 rounded-2xl p-4 sm:p-5 shadow-inner shadow-black/40 mt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm sm:text-base font-semibold text-white">Refacciones agregadas</h3>
                                    <span className="text-[11px] text-gray-400">{refacciones.length} refacción(es)</span>
                                </div>

                                {refacciones.length === 0 ? (
                                    <p className="text-xs text-gray-500">No hay refacciones agregadas.</p>
                                ) : (
                                    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                                        {refacciones.map((refa) => (
                                            <div
                                                key={refa.id}
                                                className="flex items-start gap-2 bg-gray-950/60 rounded-xl p-2.5 text-xs border border-gray-800"
                                            >
                                                <div className="flex-1 space-y-1.5">
                                                    <Input
                                                        label="Nombre"
                                                        value={refa.nombre}
                                                        onChange={(e) => handleUpdateRefaccion(refa.id, 'nombre', e.target.value)}
                                                        className="bg-gray-950 border-gray-800 text-base sm:text-[11px]"
                                                    />
                                                    <div className="grid grid-cols-3 gap-2">
                                                        <Input
                                                            label="Cant."
                                                            type="number"
                                                            value={displayNumber(refa.cantidad)}
                                                            onChange={(e) => handleUpdateRefaccion(refa.id, 'cantidad', Number(e.target.value) || 0)}
                                                            className="bg-gray-950 border-gray-800 text-base sm:text-[11px]"
                                                        />
                                                        <Input
                                                            label="Precio"
                                                            type="number"
                                                            value={displayNumber(refa.precioUnitario)}
                                                            onChange={(e) =>
                                                                handleUpdateRefaccion(refa.id, 'precioUnitario', Number(e.target.value) || 0)
                                                            }
                                                            className="bg-gray-950 border-gray-800 text-base sm:text-[11px]"
                                                        />
                                                        <div className="flex flex-col justify-end items-end text-[11px]">
                                                            <span className="text-gray-400">Total</span>
                                                            <span className="font-semibold text-white">${Number(refa.total || 0).toFixed(2)}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        dismissKeyboard();
                                                        handleRemoveRefaccion(refa.id);
                                                    }}
                                                    className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg flex-shrink-0 mt-5"
                                                >
                                                    <Icon name="Trash2" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* ========= RESUMEN ========= */}
                        <section ref={sectionRefs.resumen} data-section-id="resumen" className="scroll-mt-28">
                            <div className="bg-gradient-to-r from-gray-900 via-slate-900 to-gray-900 rounded-2xl p-4 lg:p-6 text-white shadow-[0_20px_60px_rgba(15,23,42,0.85)] border border-cyan-500/30">
                                <div className="flex items-center gap-3 lg:gap-4">
                                    <div className="w-10 h-10 lg:w-12 lg:h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-cyan-400/60">
                                        <Icon name="Calculator" size={20} className="lg:w-6 lg:h-6 text-white" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-base sm:text-lg lg:text-2xl font-bold truncate">Resumen y totales</h3>
                                        <p className="text-cyan-100 mt-1 text-xs sm:text-sm lg:text-base truncate">
                                            Revisa impuestos y total antes de generar
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-900/85 border border-gray-800 rounded-2xl p-4 sm:p-5 shadow-inner shadow-black/40 mt-4 space-y-4">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <label className="inline-flex items-center gap-2 text-sm text-gray-200 select-none">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(incluirIVA)}
                                                onChange={(e) => {
                                                    dismissKeyboard();
                                                    setIncluirIVA(e.target.checked);
                                                }}
                                                className="w-4 h-4 accent-cyan-500"
                                            />
                                            Incluir IVA
                                        </label>

                                        <label className="inline-flex items-center gap-2 text-sm text-gray-200 select-none">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(incluirISR)}
                                                onChange={(e) => {
                                                    dismissKeyboard();
                                                    setIncluirISR(e.target.checked);
                                                }}
                                                className="w-4 h-4 accent-cyan-500"
                                            />
                                            Incluir ISR
                                        </label>
                                    </div>

                                    <div className="text-xs text-gray-400">
                                        Ítems: <span className="text-gray-200 font-medium">{refacciones.length + servicios.length}</span>
                                    </div>
                                </div>

                                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                    <div className="bg-gray-950/60 p-3 rounded-xl border border-gray-800">
                                        <p className="text-gray-400 text-xs">Subtotal</p>
                                        <p className="text-white font-semibold text-base">${subtotal.toFixed(2)}</p>
                                    </div>
                                    <div className="bg-gray-950/60 p-3 rounded-xl border border-gray-800">
                                        <p className="text-gray-400 text-xs">IVA</p>
                                        <p className="text-white font-semibold text-base">${iva.toFixed(2)}</p>
                                    </div>
                                    <div className="bg-gray-950/60 p-3 rounded-xl border border-gray-800">
                                        <p className="text-gray-400 text-xs">ISR</p>
                                        <p className="text-white font-semibold text-base">${isr.toFixed(2)}</p>
                                    </div>
                                    <div className="bg-cyan-950/40 p-3 rounded-xl border border-cyan-500/30">
                                        <p className="text-cyan-300 text-xs">Total</p>
                                        <p className="text-white font-bold text-lg">${totalActual.toFixed(2)}</p>
                                    </div>
                                </div>

                                <div className="text-[11px] text-gray-500">
                                    El botón de generar se habilita cuando haya al menos 1 servicio o refacción (tieneItems).
                                </div>
                            </div>
                        </section>
                    </div>

                    {/* FOOTER */}
                    <div className="border-t border-gray-800 bg-gray-950/98 safe-area-inset-bottom px-3 sm:px-4 lg:px-6 py-3 lg:py-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-[11px] sm:text-xs text-gray-400">
                                <span>
                                    Refacciones: <span className="text-cyan-300 font-medium">{refacciones.length}</span>
                                </span>
                                <span>
                                    Servicios: <span className="text-emerald-300 font-medium">{servicios.length}</span>
                                </span>
                                <span>
                                    Total: <span className="text-cyan-300 font-semibold">${totalActual.toFixed(2)}</span>
                                </span>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-2 lg:gap-3 w-full sm:w-auto">
                                <Button
                                    variant="outline"
                                    onClick={handleSafeClose}
                                    className="w-full sm:w-auto border-gray-700 text-gray-300 hover:bg-gray-900 hover:text-white py-3 px-4 text-sm min-h-[44px]"
                                >
                                    Cerrar
                                </Button>

                                <motion.div className="w-full sm:w-auto" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            dismissKeyboard();
                                            generarPDF();
                                        }}
                                        disabled={!tieneItems}
                                        className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-xl shadow-cyan-500/40 py-3 px-6 text-sm min-h-[44px] disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <Icon name="Printer" className="w-4 h-4 mr-2 lg:w-5 lg:h-5" />
                                        Generar orden de trabajo
                                    </Button>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default BudgetModal;
